<template>
  <v-menu offset-y left v-if="user" v-model="open" :close-on-content-click="false">
    <template v-slot:activator="{ on, attrs }">
      <button
        type="button"
        class="acct-btn"
        v-bind="attrs"
        :aria-label="toLatin(user.name) + ' ' + toLatin(user.lastname || '')"
        v-on="on"
      >
        <v-avatar
          color="rgba(251, 109, 59, 0.15)"
          class="avatar-text"
          size="36"
          :class="{
            'avatar-loading': loading.questionnaires || loading.restaurantQuestionnaires,
          }"
        >
          <v-progress-circular
            v-if="loading.questionnaires || loading.restaurantQuestionnaires"
            indeterminate
            color="primary"
            size="20"
            width="2"
          />
          <span v-else style="" class="text-white">
            {{ toLatin(user.name.charAt(0).toUpperCase()) }}
            {{
              user.lastname != null ? toLatin(user.lastname.charAt(0).toUpperCase()) : ""
            }}
          </span>
        </v-avatar>
        <span v-if="showName" class="acct-btn__text">
          <b>{{ toLatin(user.name) }} {{ toLatin(user.lastname || "") }}</b>
          <span v-if="roleLabel">{{ roleLabel }}</span>
        </span>
        <v-icon v-if="showName" size="18" class="acct-btn__chev">mdi-chevron-down</v-icon>
      </button>
    </template>
    <v-card class="account-menu">
      <v-progress-linear v-if="switchingId" indeterminate color="primary" height="2" absolute />

      <v-list dense class="py-0">
        <v-list-item class="account-menu__current">
          <v-list-item-avatar size="52" color="primary" class="account-menu__current-avatar">
            {{ initials(user) }}
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="account-menu__current-name">
              {{ toLatin(user.name) }} {{ toLatin(user.lastname || "") }}
            </v-list-item-title>
            <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="my-0">
            <v-icon color="primary">mdi-check-circle</v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <template v-if="!accountsOnly && listItems.length">
        <v-divider />
        <v-list dense nav>
          <v-list-item
            v-for="(item, index) in listItems"
            :key="index"
            :to="item.to"
            @click="open = false"
          >
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <v-divider />

      <v-list dense nav>
        <v-subheader v-if="otherAccounts.length" class="account-menu__subheader">
          {{ $t("accounts_other") }}
        </v-subheader>
        <v-list-item
          v-for="account in otherAccounts"
          :key="account.id"
          :disabled="!!switchingId"
          @click="switchTo(account)"
        >
          <v-list-item-avatar size="32" color="rgba(251, 109, 59, 0.15)" class="avatar-text">
            {{ initials(account) }}
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>
              {{ toLatin(account.name) }} {{ toLatin(account.lastname) }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="!account.token" class="error--text">
              {{ $t("accounts_sign_in_again") }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-else>
              {{ account.context ? toLatin(account.context) : account.email }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="my-0">
            <v-btn
              icon
              small
              :title="$t('accounts_remove')"
              @click.stop="removeAccount(account)"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>

        <v-list-item :disabled="!!switchingId" @click="addAccount()">
          <v-list-item-icon>
            <v-icon>mdi-account-plus-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t("accounts_add") }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider />

      <v-list dense nav>
        <v-list-item :disabled="!!switchingId" @click="logout">
          <v-list-item-icon>
            <v-icon>mdi-power</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t("logout") }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="otherAccounts.length" :disabled="!!switchingId" @click="logoutAll">
          <v-list-item-icon>
            <v-icon>mdi-account-multiple-remove-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t("accounts_logout_all") }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
import { mapState } from "vuex";
import companyService from "@/services/company";
import restaurantService from "@/services/restaurant";
import hasSnackbar from "@/mixins/hasSnackbar";
import { accountInitials, startAddingAccount } from "@/utils/accounts";

export default {
  name: "AppAvatarMenu",
  mixins: [hasSnackbar],
  props: {
    // samo prebacivanje naloga, bez profila/QR/izvještaja — za restoran
    // korisnike kojima AppBar ranije nije prikazivao avatar
    accountsOnly: {
      type: Boolean,
      default: false,
    },
    // na širokom ekranu pored avatara ime i uloga
    showName: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      switchingId: null,
      questionares: [],
      hasQuestionare: false,
      loading: {
        questionnaires: false,
        restaurantQuestionnaires: false,
      },
    };
  },
  computed: {
    ...mapState(["company"]),
    user() {
      return this.$store.state.user.user;
    },
    otherAccounts() {
      return this.$store.state.accounts.list.filter((a) => a.id !== this.user.id);
    },
    roleLabel() {
      const key = "nav_role_" + this.user?.type;
      return this.$te(key) ? this.$t(key) : "";
    },

    listItems() {
      let routes = [
        { title: "profile", icon: "mdi-account", to: { name: "profil" } },
        { title: "qr_code", icon: "mdi-qrcode", to: { name: "qrShow" } },
        { title: "info", icon: "mdi-information", to: { name: "info" } },
      ];
      if (this.user?.type === "supplier") {
        routes = [];
      }
      const isUserCompanyType = this.user?.type === "company";

      if (typeof this.company?.settings == "string") {
        if (
          this.company?.settings &&
          JSON.parse(this.company.settings).can_see_report &&
          JSON.parse(this.company.settings).can_see_report != null
        ) {
          routes.splice(2, 0, {
            title: "report",
            icon: "mdi-chart-timeline-variant",
            to: {
              name: isUserCompanyType ? "izvestajFirma" : "izvestaj-naplata",
            },
          });
        }
      } else {
        if (this.company?.settings?.can_see_report != null) {
          routes.splice(2, 0, {
            title: "report",
            icon: "mdi-chart-timeline-variant",
            to: {
              name: isUserCompanyType ? "izvestajFirma" : "izvestaj-naplata",
            },
          });
        }
      }

      if (this.questionares.length) {
        this.questionares.forEach((q, i) => {
          routes.push({
            title: "Anketa " + (i + 1),
            icon: "mdi-information",
            to: { name: "questionnaire", params: { q } },
          });
        });
      }
      return routes;
    },
  },
  methods: {
    initials(account) {
      return this.toLatin(accountInitials(account));
    },
    // Login stranica se otvara ispočetka (prazan store), a aktivni nalog ostaje
    // sačuvan — "Nazad" na login stranici vraća na njega bez lozinke. Router
    // guard je taj koji poslije reload-a preusmjeri na login: promjena hash-a
    // prije reload-a ne radi, jer je router (još sa starim korisnikom u
    // store-u) odmah vrati na početnu rutu.
    addAccount(email) {
      startAddingAccount(email);
      window.location.reload();
    },
    async switchTo(account) {
      if (!account.token) {
        this.addAccount(account.email);
        return;
      }
      this.switchingId = account.id;
      try {
        const switched = await this.$store.dispatch("accounts/switchTo", account);
        if (!switched) {
          this.showErrorSnackbar(this.$t("accounts_session_expired"));
          this.addAccount(account.email);
        }
      } catch (err) {
        this.showErrorSnackbar(this.$t("accounts_switch_failed"));
      } finally {
        this.switchingId = null;
      }
    },
    removeAccount(account) {
      this.$store.dispatch("accounts/forget", account.id);
    },
    logout() {
      this.open = false;
      this.$store.dispatch("accounts/signOut");
    },
    logoutAll() {
      this.open = false;
      this.$store.dispatch("accounts/signOutAll");
    },
    setupQuestionnaires() {
      this.questionares = [];
      if (this.accountsOnly) return;
      if (!this.company?.id) return;
      if (this.loading.questionnaires) return;

      this.loading.questionnaires = true;
      companyService
        .getQuestionnaire(this.company.id)
        .then((res) => {
          if (res.data?.length) {
            this.hasQuestionare = true;
            this.questionares.push(
              res.data.filter((q) => q.roles.includes(this.user.type))
            );
          } else {
            this.hasQuestionare = false;
          }
        })
        .catch((err) => {
          console.error("Error loading questionnaires:", err);
          if (err.response?.status === 429) {
            this.hasQuestionare = true;
          } else {
            this.hasQuestionare = false;
            this.showErrorSnackbar(err, this.$t("error_loading_questionnaires"));
          }
        })
        .finally(() => {
          this.loading.questionnaires = false;
        });
    },
    setupRestaurantQuestionnaires() {
      if (this.accountsOnly) return;
      if (!this.$store.state.restaurant?.id) return;
      if (this.loading.restaurantQuestionnaires) return;

      this.loading.restaurantQuestionnaires = true;
      restaurantService
        .getRestaurantQuestionnaires(this.$store.state.restaurant.id)
        .then((res) => {
          this.hasQuestionare = true;
          this.questionares.push(
            res.data.filter((q) => q.roles.includes(this.user.type))
          );
        })
        .catch((error) => {
          console.error("Error loading restaurant questionnaires:", error);
          this.hasQuestionare = this.hasQuestionare || false;
          this.showErrorSnackbar(error, this.$t("error_loading_questionnaires"));
        })
        .finally(() => {
          this.loading.restaurantQuestionnaires = false;
        });
    },
  },
  watch: {
    company: {
      handler(newCompany) {
        if (newCompany) {
          this.setupQuestionnaires();

          // Setup restaurant questionnaires if restaurant exists
          if (this.$store.state.restaurant) {
            this.setupRestaurantQuestionnaires();
          }
        }
      },
      immediate: true,
    },
    "$store.state.restaurant"(newRestaurant) {
      if (newRestaurant && this.company) {
        this.setupRestaurantQuestionnaires();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_variables.scss";

.account-menu {
  width: 300px;
  max-width: calc(100vw - 32px);
}

.account-menu__current {
  background-color: $primary-light;
  padding-top: 6px;
  padding-bottom: 6px;
}

.account-menu__current-avatar {
  color: #fff;
  font-size: 18px;
  font-weight: 600;
}

.account-menu__current-name {
  font-size: 16px !important;
  font-weight: 700;
  line-height: 1.4;
}

.account-menu__subheader {
  height: 28px;
  padding: 0 8px;
}

.avatar-text {
  color: #9a3412;
  font-size: 13px;
  font-weight: 700;
}

.acct-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 4px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  &:hover,
  &[aria-expanded="true"] {
    background: #f7f1ed;
    border-color: #f0e2d9;
  }
  &:focus-visible {
    outline: 2px solid #1d4ed8;
    outline-offset: 1px;
  }
  &__text {
    display: grid;
    line-height: 1.2;
    min-width: 0;
    b {
      font-size: 0.84rem;
      font-weight: 700;
      color: #1c1410;
      white-space: nowrap;
    }
    span {
      font-size: 0.72rem;
      color: #6b5a50;
      white-space: nowrap;
    }
  }
  &__chev {
    color: #6b5a50 !important;
    margin-right: 4px;
  }
}

.avatar-loading {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
</style>
